// =============================================
// src/components/WorkflowStep.jsx
// =============================================

import React, { useEffect, useState } from "react";
import { CheckCircle, ChevronDown, ChevronRight, Clock, Loader2, XCircle } from "lucide-react";
import { styles } from "../utils/constants";

export default function WorkflowStep({ step, index, isActive }) {
  const [status, setStatus] = useState("pending"); // pending, running, completed, error
  const [expanded, setExpanded] = useState(false);

  // When a step becomes active, expand and show "running"
  useEffect(() => {
    if (!isActive || status !== "pending") return;
    setExpanded(true);
    setStatus("running");
  }, [isActive, status]);

  // NEW: Status follows the actual output coming back from your server
  useEffect(() => {
    if (!isActive) return;

    const out = step?.output || "";

    // If backend filled an error string, mark as error
    if (typeof out === "string" && out.startsWith("ERROR:")) {
      setStatus("error");
      return;
    }

    // If backend filled output, mark as completed
    if (out && out.length > 0) {
      setStatus("completed");
      return;
    }

    // Otherwise, still running while active
    setStatus("running");
  }, [isActive, step?.output]);

  const getStatusIcon = () => {
    switch (status) {
      case "running":
        return <Loader2 size={18} className="spinning" />;
      case "completed":
        return <CheckCircle size={18} color="#10b981" />;
      case "error":
        return <XCircle size={18} color="#ef4444" />;
      default:
        return <Clock size={18} color="#94a3b8" />;
    }
  };

  const getStatusStyle = () => {
    switch (status) {
      case "running":
        return { backgroundColor: "#fef3c7", color: "#92400e" };
      case "completed":
        return { backgroundColor: "#dcfce7", color: "#15803d" };
      case "error":
        return { backgroundColor: "#fee2e2", color: "#991b1b" };
      default:
        return { backgroundColor: "#f1f5f9", color: "#64748b" };
    }
  };

  return (
    <div
      style={{
        ...styles.stepCard,
        borderColor:
          status === "completed"
            ? "#10b981"
            : status === "running"
            ? "#f59e0b"
            : status === "error"
            ? "#ef4444"
            : "#e2e8f0",
        boxShadow:
          status === "running"
            ? "0 0 0 3px rgba(245,158,11,0.1)"
            : status === "completed"
            ? "0 0 0 3px rgba(16,185,129,0.1)"
            : status === "error"
            ? "0 0 0 3px rgba(239,68,68,0.1)"
            : styles.stepCard.boxShadow,
      }}
    >
      <div style={styles.stepHeader} onClick={() => setExpanded(!expanded)}>
        <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: "12px" }}>
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <span style={{ fontSize: "16px", fontWeight: "600", color: "#0f172a" }}>
            Step {index + 1}: {step.title}
          </span>
        </div>
        <div style={{ ...styles.statusBadge, ...getStatusStyle() }}>
          {getStatusIcon()}
          <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
        </div>
      </div>

      {expanded && (
        <div style={styles.stepContent}>
          <div style={styles.toolName}>{step.tool}</div>

          <div style={{ marginBottom: "16px" }}>
            <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#334155" }}>
              Description:
            </h4>
            <p style={{ fontSize: "14px", color: "#64748b", margin: 0, lineHeight: "1.6" }}>
              {step.description}
            </p>
          </div>

          {step.input && (
            <div style={{ marginBottom: "16px" }}>
              <h4 style={{ fontSize: "14px", fontWeight: "600", marginBottom: "8px", color: "#334155" }}>
                Parameters:
              </h4>
              <pre
                style={{
                  fontSize: "13px",
                  color: "#475569",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  lineHeight: "1.6",
                  backgroundColor: "#f8fafc",
                  padding: "12px",
                  borderRadius: "6px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {step.input}
              </pre>
            </div>
          )}

          {status === "running" && (
            <div
              style={{
                padding: "20px",
                backgroundColor: "#fef3c7",
                borderRadius: "8px",
                textAlign: "center",
                marginTop: "16px",
                border: "1px solid #fbbf24",
              }}
            >
              <Loader2 size={24} className="spinning" style={{ margin: "0 auto", color: "#92400e" }} />
              <p style={{ marginTop: "12px", fontSize: "14px", color: "#92400e", fontWeight: "500" }}>
                Executing {step.tool}...
              </p>
            </div>
          )}

          {(status === "completed" || status === "error") && step.output && (
            <div
              style={{
                ...styles.resultBox,
                backgroundColor: status === "error" ? "#fef2f2" : "#f0fdf4",
                border: status === "error" ? "1px solid #ef4444" : "1px solid #10b981",
              }}
            >
              <h4
                style={{
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  color: status === "error" ? "#991b1b" : "#047857",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {status === "error" ? (
                  <>
                    <XCircle size={16} color="#ef4444" /> Step error
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} color="#10b981" /> Step output
                  </>
                )}
              </h4>

              <pre
                style={{
                  fontSize: "13px",
                  color: status === "error" ? "#7f1d1d" : "#064e3b",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  lineHeight: "1.6",
                  backgroundColor: "white",
                  padding: "12px",
                  borderRadius: "6px",
                  border: status === "error" ? "1px solid #fecaca" : "1px solid #d1fae5",
                }}
              >
                {step.output}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
